import {
  Col,
  Form,
  Input,
  Row,
  Typography,
  Button as AntButton,
  Divider,
} from "antd";
import { Button, LanguageChange } from "shared/components";
import { Trans, useTranslation } from "react-i18next";
import { signIn, fetchAuthSession } from "aws-amplify/auth";
import { RouteComponentProps } from "react-router";
import { useDispatch } from "react-redux";
import { getLoggedUser } from "explorer/reducers/users";
import { ReactComponent as Logo } from "shared/assets/images/logo-strm.svg";
import { ReactComponent as MailIcon } from "shared/assets/images/icon-mail.svg";
import { ReactComponent as LockIcon } from "shared/assets/images/icon-lock.svg";
import { ReactComponent as LoginIcon } from "shared/assets/images/icon-log-in.svg";
import DrumGirl from "shared/assets/images/drum-girl-purple-tall.png";
import { useEffect, useState } from "react";
import {
  DISTRO_PARAM,
  PRICING_PARAM,
  TOKEN,
} from "shared/constants/StorageConsts";
import { loginReduced } from "explorer/reducers/auth";
import { hasHeroPending, hasQuizPending } from "shared/helpers/auth-functions";
import { isMobile } from "react-device-detect";
import { Link } from "react-router-dom";
import LoginTheme from "shared/themes/LoginTheme";
import { LoadingOutlined } from "@ant-design/icons";
import { getLoggedUserArtist } from "explorer/reducers/artistas";
import { toast } from "react-toastify";
import "./Login.scss";
import "../cadastre-se/CadastreSe.scss";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { LoginButtonClickEventName } from "event-tracker/button-click-event-name";
import { getStrmClassVideos } from "explorer/reducers/strm-class";
import { getMonitoredData } from "explorer/reducers/monitored-data";
import { getUserSubscription } from "explorer/reducers/subscription";

const Login: React.FC<RouteComponentProps> = (props) => {
  const { history, location } = props;
  const urlSearch = new URLSearchParams(location.search);
  const [loading, setLoading] = useState<boolean>(false);
  const [pricingFlowRoute] = useState(urlSearch.get("pricing") === "true");
  const { t } = useTranslation("explorer");
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const onFinish = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      localStorage.clear();

      const { isSignedIn, nextStep } = await signIn({
        username: values.email,
        password: values.password,
      });
      if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
        history.push("/verificacao", {
          name: values.name,
          login: values.email,
        });
        return;
      }
      if (isSignedIn) {
        const { idToken } = (await fetchAuthSession()).tokens ?? {};

        localStorage.setItem(TOKEN, idToken?.toString() ?? "");
        dispatch(getLoggedUser());
        dispatch(getLoggedUserArtist());
        dispatch(loginReduced());
        dispatch(getStrmClassVideos());
        dispatch(getMonitoredData());
        // dispatch(getUserDistroToken());
        dispatch(getUserSubscription());
        const distroFlow = sessionStorage.getItem(DISTRO_PARAM) === "true";
        const pricingFlow = sessionStorage.getItem(PRICING_PARAM) === "true";
        if (hasQuizPending()) return history.push("/quiz");
        if (hasHeroPending()) return history.push("/strm-hero");
        const returnURL = sessionStorage.getItem("returnURL");
        sessionStorage.removeItem("returnURL");
        if (distroFlow) {
          history.push("/distribua");
          return;
        }
        if (pricingFlow || pricingFlowRoute) {
          history.push("/pricing");
          return;
        }
        if (returnURL) {
          history.push(returnURL);
          return;
        }
        history.push("/", { login: true });
      }
    } catch (error: any) {
      //redirect usuario nao verificado
      if (error.name === "UserNotConfirmedException") {
        history.push("/verificacao", {
          name: values.name,
          login: values.email,
        });
      }
      if (error.name === "NotAuthorizedException") {
        toast.error<string>(t("pages.Login.naoAutorizado"), {
          autoClose: 10000,
        });
      } else toast.error<string>(error.message);
    }

    setLoading(false);
  };

  const instagramWebView = window.navigator.userAgent
    .toLowerCase()
    .includes("instagram");

  useEffect(() => {
    dataLayerViewedEvent(tagManagerEvent.Login.S4A_LOGIN);
  }, []);

  return (
    <LoginTheme>
      <Row className="login">
        {isMobile && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <LanguageChange.SelectLanguageChange />
          </div>
        )}
        <Col
          xs={isMobile ? 24 : 10}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <div className="container" style={{ padding: isMobile ? 12 : 0 }}>
            <Row className="fedarated-buttons" gutter={18}>
              <Col xs={24}>
                <div className="logo-region">
                  <Logo
                    className="logo"
                    style={{ width: isMobile ? 80 : 100 }}
                  />
                  <span style={{ fontSize: isMobile ? 14 : 28 }}>
                    for artists
                  </span>
                </div>
              </Col>
              {isMobile && (
                <p className="mobile-copy">
                  <Trans i18nKey="pages.Login.exploreSuaCarreiraMobile" />
                </p>
              )}
              <Col span={24}>
                <Typography.Text className="text social">
                  {t("pages.CadastreSe.socialText")}
                </Typography.Text>
              </Col>
              <Col xs={12} hidden={instagramWebView}>
                <Button.Google
                  onClick={() => {
                    dataLayerClickedEvent(
                      LoginButtonClickEventName.LOGIN_GOOGLE,
                      tagManagerEvent.Login.S4A_LOGIN,
                    );
                  }}
                />
              </Col>
              <Col span={instagramWebView ? 24 : 12}>
                <Button.Facebook
                  onClick={() => {
                    dataLayerClickedEvent(
                      LoginButtonClickEventName.LOGIN_FACEBOOK,
                      tagManagerEvent.Login.S4A_LOGIN,
                    );
                  }}
                />
              </Col>
            </Row>
            <Divider className="divider">{t("pages.Login.ou")}</Divider>
            <Row>
              <Col span={24}>
                <Form
                  layout={"vertical"}
                  form={form}
                  onFinish={onFinish}
                  autoComplete="off"
                >
                  <div className="content">
                    <div className="input">
                      <Form.Item
                        name="email"
                        rules={[
                          {
                            required: true,
                            message: t("pages.Login.emailObrigatorio"),
                          },
                          {
                            type: "email",
                            message: t("pages.CadastreSe.emailValido"),
                          },
                        ]}
                      >
                        <Input
                          placeholder={t("pages.Login.email")}
                          size="large"
                          prefix={<MailIcon />}
                          data-testid="input-email"
                        />
                      </Form.Item>

                      <Form.Item
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: t("pages.Login.senhaObrigatorio"),
                          },
                        ]}
                      >
                        <Input.Password
                          placeholder={t("pages.Login.senha")}
                          size="large"
                          prefix={<LockIcon />}
                          data-testid="input-password"
                        />
                      </Form.Item>
                    </div>

                    <div className="enter-button">
                      <button
                        className="botao-roxo"
                        disabled={loading}
                        onClick={() => {
                          dataLayerClickedEvent(
                            LoginButtonClickEventName.SIGN_IN,
                            tagManagerEvent.Login.S4A_LOGIN,
                          );
                        }}
                        type="submit"
                      >
                        {loading ? (
                          <LoadingOutlined style={{ fontSize: 25 }} spin />
                        ) : (
                          <span className="login-button-label">
                            {t("pages.Login.entrar")}
                          </span>
                        )}
                        <LoginIcon />
                      </button>
                    </div>
                    <div className="esqueci-senha">
                      <AntButton
                        type="link"
                        htmlType="button"
                        onClick={() => {
                          dataLayerClickedEvent(
                            LoginButtonClickEventName.RECOVERY_PASSWORD,
                            tagManagerEvent.Login.S4A_LOGIN,
                          );
                          history.push("/reset-senha");
                        }}
                        className="button-reset-senha"
                      >
                        {t("pages.Login.esqueciSenha")}
                      </AntButton>
                    </div>
                  </div>
                </Form>
              </Col>
            </Row>
            <Row className="row-register">
              <Col span={24}>
                <div className="text-register back-login">
                  {t("pages.Login.noAccountMessage")}
                  <Link
                    style={{ marginLeft: 6 }}
                    to="/quiz"
                    onClick={() => {
                      dataLayerClickedEvent(
                        LoginButtonClickEventName.SIGN_UP,
                        tagManagerEvent.Login.S4A_LOGIN,
                      );
                    }}
                  >
                    {t("pages.Login.cadastre")}
                  </Link>
                </div>
              </Col>
            </Row>
            <span
              style={{
                display: "flex",
                borderBottom: "1px solid #272727",
                marginTop: 22,
              }}
            />

            <span className="outras-plataformas-titulo">
              {t("pages.Login.acesseOutraPlataforma")}
            </span>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <a
                href={process.env.REACT_APP_STRM_INVESTOR_URL}
                className="outras-plataformas"
              >
                <span>
                  <Logo style={{ width: 67 }} />
                  for Labels
                </span>
              </a>
            </div>
          </div>
        </Col>
        <Col xs={14} hidden={isMobile}>
          <div className="caixa-copy">
            <div className="copy-content">
              {!isMobile && <LanguageChange.SelectLanguageChange />}
              <img src={DrumGirl} alt="girl playing drum" />
              <p className="copy">{t("pages.Login.exploreSuaCarreira")}</p>
            </div>
          </div>
        </Col>
      </Row>
    </LoginTheme>
  );
};

export default Login;
